import { useMemo } from 'react';
import moment from 'moment';
import 'moment-timezone';
import {
  formatTHBFromCents,
  getAllFeeAmounts,
  getBookingStatusIntlKey,
  getEffectiveLocale,
  getInitialImage,
  getLocalizedListingData,
  getStatusStyle,
  getSquareSmallImageUrl,
  hasPendingRescheduleRequest,
} from '../../../util/genericHelpers';
import { BookingStatus, CHARGE_STATUS, PAYMENT_METHODS, TRANSITIONS } from '../../../util/enums';
import {
  getEffectivePaymentMethod,
  getProcess,
  isPayLaterPaymentMethod,
  isPayLaterTransaction,
  resolveLatestProcessName,
} from '../../../transactions/transaction';
import {
  lineItemsTotal,
  nonCommissionReversalLineItems,
} from '../../../components/OrderBreakdown/LineItemRefundMaybe';
import { ensureListing } from '../../../util/data';
import { canLeaveReview } from '../../../util/transactionReview';
import {
  isCustomOfferInquiryTransition,
  isCustomOfferBookingTransaction,
  isCustomOfferPaymentCompleted,
  isCustomOfferPaymentPending,
  isCustomOfferPaymentRequestTransition,
  isOmisePaymentPaid,
} from '../../../util/customOffers';
import {
  getEarningsStatusIntlKey,
  resolveEarningsStatus,
} from '../../../util/payoutStatus';

// ----------------------------
// Booking categories
// ----------------------------
export const BookingCategory = {
  PAST: 'past',
  CURRENT: 'current',
};

export const BOOKING_CATEGORY_INTL = {
  [BookingCategory.PAST]: 'BookingCategory.past',
  [BookingCategory.CURRENT]: 'BookingCategory.current',
};

export const ATTENTION_NOTIFICATION_VARIANTS = {
  PAYMENT_PENDING: 'payment-pending',
};

// ----------------------------
// Transition groups
// ----------------------------
const COMPLETED = new Set([
  TRANSITIONS.COMPLETE,
  TRANSITIONS.OPERATOR_COMPLETE,
  TRANSITIONS.MARK_RECEIVED_FROM_DISPUTED,
  TRANSITIONS.PAYOUT_COMPLETE,
  TRANSITIONS.OPERATOR_PAYOUT_COMPLETE,
]);

const REQUESTED = new Set([
  TRANSITIONS.REQUEST_PAYMENT,
  TRANSITIONS.REQUEST_PAY_LATER_PAYMENT,
  TRANSITIONS.REQUEST_PAYMENT_AFTER_INQUIRY,
  TRANSITIONS.REQUEST_PAY_LATER_PAYMENT_AFTER_INQUIRY,
  TRANSITIONS.RETRY_PAYMENT_AFTER_FAILED,
  TRANSITIONS.RETRY_PAY_LATER_PAYMENT_AFTER_FAILED,
]);

const PAYMENT_EXPIRED = new Set([
  TRANSITIONS.PAYMNENT_WINDOW_PASSED,
  TRANSITIONS.OPERATOR_PAYMNENT_WINDOW_PASSED,
]);

const EXPIRED = new Set([
  TRANSITIONS.EXPIRE,
  TRANSITIONS.EXPIRE_PAYMENT,
  TRANSITIONS.ACCEPTANCE_WINDOW_PASSED,
  TRANSITIONS.PAY_LATER_ACCEPTANCE_WINDOW_PASSED,
  TRANSITIONS.AUTO_DECLINE_BOOKING_TIME_STARTS,
  TRANSITIONS.OPERATOR_ACCEPTANCE_WINDOW_PASSED,
  TRANSITIONS.OPERATOR_PAY_LATER_ACCEPTANCE_WINDOW_PASSED,
]);

const CANCELLED = new Set([
  TRANSITIONS.CANCEL_FROM_DISPUTED,
  TRANSITIONS.CANCEL_BY_PROVIDER,
  TRANSITIONS.CANCEL_BY_CUSTOMER,
  TRANSITIONS.CANCEL_BOOKING_REQUEST_PAYMENT,
  TRANSITIONS.CANCEL_BOOKING_REQUEST,
  TRANSITIONS.CANCEL_PAY_LATER_BOOKING_REQUEST,
  TRANSITIONS.CANCEL,
  TRANSITIONS.AUTO_CANCEL_FROM_DISPUTED,
  TRANSITIONS.INITIATE_REFUND,
  TRANSITIONS.OPERATOR_COMPLETE_AFTER_CANCEL,
  TRANSITIONS.COMPLETE_AFTER_CANCEL,
  TRANSITIONS.REQUEST_ACCOUNT_INFO_BY_PROVIDER,
  TRANSITIONS.ADD_ACCOUNT_INFO_BY_CUSTOMER,
]);

const DISPUTED = new Set([TRANSITIONS.DISPUTE, TRANSITIONS.OPERATOR_DISPUTE]);

const REVIEWED = new Set([
  TRANSITIONS.REVIEW_1_BY_PROVIDER,
  TRANSITIONS.REVIEW_2_BY_PROVIDER,
  TRANSITIONS.REVIEW_1_BY_CUSTOMER,
  TRANSITIONS.REVIEW_2_BY_CUSTOMER,
]);

const PAYMENT_COMPLETED = new Set([
  TRANSITIONS.CONFIRM_PAYMENT,
  TRANSITIONS.OPERATOR_CONFIRM_PAYMENT,
  TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT,
  TRANSITIONS.OPERATOR_CONFIRM_PAY_LATER_PAYMENT,
]);

const PAY_LATER_PAYMENT_COMPLETED = new Set([
  TRANSITIONS.CONFIRM_PAY_LATER_PAYMENT,
  TRANSITIONS.OPERATOR_CONFIRM_PAY_LATER_PAYMENT,
]);

const DECLINED = new Set([
  TRANSITIONS.DECLINE,
  TRANSITIONS.OPERATOR_DECLINE,
  TRANSITIONS.DECLINE_PAY_LATER,
  TRANSITIONS.OPERATOR_DECLINE_PAY_LATER,
]);

const RESCHEDULED = new Set([TRANSITIONS.RESCHEDULE_BOOKING]);
const FAILED_PAYMENT = new Set([TRANSITIONS.PAYMENT_FAILED]);

const ACCEPT = new Set(
  [
    TRANSITIONS.ACCEPT,
    TRANSITIONS.OPERATOR_ACCEPT,
    TRANSITIONS.ACCEPT_PAY_LATER,
    TRANSITIONS.OPERATOR_ACCEPT_PAY_LATER,
    TRANSITIONS.COMPLETE_CUSTOM_OFFER_PAYMENT,
    TRANSITIONS.CONFIRM_CUSTOM_OFFER_PAY_LATER_PAYMENT,
  ].filter(Boolean)
);

const PAY_LATER_ACCEPTED = new Set([
  TRANSITIONS.ACCEPT_PAY_LATER,
  TRANSITIONS.OPERATOR_ACCEPT_PAY_LATER,
]);

const RESCHEDULE_ACCPETED = new Set([TRANSITIONS.ACCEPT_RESCHEDULE_BOOKING]);
const RESCHEDULE_DECLINED = new Set([TRANSITIONS.DECLINE_RESCHEDULE_BOOKING]);

const PENDING = new Set([TRANSITIONS.TRANSITION_RESCHEDULE_BOOKING]);

const TERMINAL_PROCESS_STATUSES = new Set([
  BookingStatus.CANCELLED,
  BookingStatus.DECLINED,
  BookingStatus.DISPUTED,
  BookingStatus.COMPLETED,
  BookingStatus.REVIEWED,
]);

const COMPLETED_BOOKING_STATUSES = new Set([BookingStatus.COMPLETED, BookingStatus.REVIEWED]);

const getTransactionProcess = transaction => {
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);

  if (!processName) {
    return null;
  }

  try {
    return getProcess(processName);
  } catch (e) {
    return null;
  }
};

export const getTransactionProcessState = transaction => {
  const process = getTransactionProcess(transaction);
  return process ? process.getState(transaction) : null;
};

const hasBookingDateValue = value => value !== null && value !== undefined && value !== '';

export const isAwaitingBookingDetails = transaction => {
  const bookingAttrs = transaction?.booking?.attributes;

  if (!bookingAttrs) {
    return false;
  }

  return (
    !hasBookingDateValue(bookingAttrs.displayStart) || !hasBookingDateValue(bookingAttrs.displayEnd)
  );
};

export const isCustomerPayLaterPaymentActionable = (transaction, isCustomer = false) => {
  if (!transaction || !isCustomer) {
    return false;
  }

  if (!isPayLaterTransaction(transaction)) {
    return false;
  }

  if (isOmisePaymentPaid(transaction)) {
    return false;
  }

  const process = getTransactionProcess(transaction);
  const processState = process ? process.getState(transaction) : null;
  const customerAttentionStates = Object.values(process?.statesNeedingCustomerAttention || {});

  return !!processState && customerAttentionStates.includes(processState);
};

// After explicit custom-offer status, Sharetribe process state is the primary lifecycle source.
// Payment metadata only resolves an accepted-pending-payment transition lag.
const getProcessStatus = transaction => {
  if (!transaction) {
    return null;
  }

  const process = getTransactionProcess(transaction);
  const processState = process ? process.getState(transaction) : null;

  if (!process || !processState) {
    return null;
  }

  if (processState === process.states.PENDING_PAYMENT) {
    return BookingStatus.PENDING_PAYMENT;
  }

  if (processState === process.states.PENDING_PROVIDER_ACCEPTANCE) {
    return BookingStatus.PENDING;
  }

  if (processState === process.states.ACCEPTED_PENDING_PAYMENT) {
    return isOmisePaymentPaid(transaction)
      ? BookingStatus.CONFIRMED
      : BookingStatus.PENDING_PAYMENT;
  }

  if (processState === process.states.PREAUTHORIZED) {
    return BookingStatus.PENDING;
  }

  if (processState === process.states.ACCEPTED) {
    return BookingStatus.CONFIRMED;
  }

  if (
    processState === process.states.PAYMENT_EXPIRED ||
    processState === process.states.EXPIRED ||
    processState === process.states.CANCELLED
  ) {
    return BookingStatus.CANCELLED;
  }

  if (processState === process.states.DECLINED) {
    return BookingStatus.DECLINED;
  }

  if (processState === process.states.DISPUTED) {
    return BookingStatus.DISPUTED;
  }

  if (processState === process.states.COMPLETED || processState === process.states.DELIVERED) {
    return BookingStatus.COMPLETED;
  }

  if (
    processState === process.states.REVIEWED ||
    processState === process.states.REVIEWED_BY_CUSTOMER ||
    processState === process.states.REVIEWED_BY_PROVIDER
  ) {
    return BookingStatus.REVIEWED;
  }

  return null;
};

// ----------------------------
// Transition → status resolver
// ----------------------------
const getStateFromTransition = (lastTransition, paymentMethod, transaction, isCustomer = false) =>
  getStatusByTransition(lastTransition, paymentMethod, isCustomer, transaction).status;

// ----------------------------
// Create row data from a transaction
// ----------------------------
export const getAttentionNotificationData = ({
  bookingDate,
  bookingSlot,
  isAttentionNotification,
  isCustomer,
  latestActivityRelative,
  status,
}) => {
  if (!(isAttentionNotification && isCustomer && status === 'payment pending')) {
    return null;
  }

  return {
    bookingDate,
    bookingSlot,
    relativeTime: latestActivityRelative,
    variant: ATTENTION_NOTIFICATION_VARIANTS.PAYMENT_PENDING,
  };
};

const createBookingData = (
  transaction,
  currentUser,
  attentionTxIdSet = new Set(),
  isEarnings = false
) => {
  if (!transaction) return null;

  const { booking, listing, provider, customer, reviews } = transaction || {};
  const bookingAttrs = booking?.attributes || {};

  const locale = getEffectiveLocale(currentUser);
  const safeListing = ensureListing(listing);
  const { title: localizedTitle, publicData: localizedPublicData } = getLocalizedListingData(
    safeListing,
    locale
  );

  const timeZone = safeListing?.attributes?.availabilityPlan?.timezone;
  const tz = timeZone || moment.tz.guess();

  const { displayStart, displayEnd } = bookingAttrs;

  const { omiseChargeStatus, payinTotalAmount, payoutTotalAmount } =
    transaction?.attributes?.metadata || {};

  const lastTransition = transaction?.attributes?.lastTransition;
  const paymentMethod = getEffectivePaymentMethod(transaction);

  const isDeclineTranistions = transaction?.attributes?.transitions?.some(
    t =>
      t?.transition === TRANSITIONS.DECLINE ||
      t?.transition === TRANSITIONS.OPERATOR_DECLINE ||
      t?.transition === TRANSITIONS.DECLINE_PAY_LATER ||
      t?.transition === TRANSITIONS.OPERATOR_DECLINE_PAY_LATER
  );

  const { refundProviderCommission, refundCustomerCommission, refundCustomerOmiseFee } =
    getAllFeeAmounts(transaction?.attributes?.lineItems) || {};

  const isHalfRefund = omiseChargeStatus === CHARGE_STATUS.HALF_REFUND;

  const refundLineItems = nonCommissionReversalLineItems(transaction?.attributes?.lineItems);
  const refund = lineItemsTotal(refundLineItems, 'THB', isHalfRefund);
  const listingRefund = refundLineItems.length > 0 ? Math.abs(refund.amount) : null;

  const validProviderCommissionRefundAmount =
    omiseChargeStatus === CHARGE_STATUS.HALF_REFUND
      ? refundProviderCommission / 2
      : refundProviderCommission;

  const isCustomer = currentUser?.id?.uuid === customer?.id?.uuid;
  const transactionRole = !currentUser?.id?.uuid
    ? null
    : isCustomer
    ? 'customer'
    : currentUser?.id?.uuid === provider?.id?.uuid
    ? 'provider'
    : null;
  const transactionStatus = getStateFromTransition(
    lastTransition,
    paymentMethod,
    transaction,
    isCustomer
  );
  const resolvedEarningsStatus = resolveEarningsStatus(transaction);
  const status = isEarnings ? resolvedEarningsStatus : transactionStatus;
  const label = isEarnings
    ? getEarningsStatusIntlKey(resolvedEarningsStatus)
    : getBookingStatusIntlKey(status);

  const start = displayStart ? moment(displayStart) : null;
  const end = displayEnd ? moment(displayEnd) : null;

  const startZ = start ? start.clone().tz(tz) : null;
  const endZ = end ? end.clone().tz(tz) : null;

  const formatRangeInTZ = (s, e) => {
    if (!s || !e) return 'Awaiting details';
    const sameDay = s.isSame(e, 'day');
    const startStr = s.format('MMM DD, hh:mm a');
    const endStr = sameDay ? e.format('hh:mm a') : e.format('MMM DD, hh:mm a');
    return `${startStr} – ${endStr}`;
  };

  const payinTotal =
    omiseChargeStatus === CHARGE_STATUS.NO_REFUND
      ? (payinTotalAmount || 0) * 100
      : omiseChargeStatus === CHARGE_STATUS.HALF_REFUND
      ? (listingRefund || 0) +
        Math.abs(refundCustomerCommission || 0) +
        Math.abs(refundCustomerOmiseFee || 0)
      : omiseChargeStatus === CHARGE_STATUS.REFUNDED
      ? Math.abs(refundCustomerCommission || 0) + Math.abs(refundCustomerOmiseFee || 0)
      : transaction?.attributes?.payinTotal?.amount || 0;

  const payoutTotal =
    omiseChargeStatus === CHARGE_STATUS.NO_REFUND
      ? payoutTotalAmount || 0
      : omiseChargeStatus === CHARGE_STATUS.HALF_REFUND
      ? Math.max(0, (listingRefund || 0) - (validProviderCommissionRefundAmount || 0))
      : transaction?.attributes?.payoutTotal?.amount || 0;

  const paymentAmount = isCustomer ? payinTotal : payoutTotal;

  const durationType = transaction?.attributes?.protectedData?.bookingModalDetails?.durationType;
  const overTime = transaction?.attributes?.protectedData?.bookingModalDetails?.overTime;
  const messageNotification = transaction?.messageNotification || null;

  const userName = isCustomer
    ? provider?.attributes?.profile?.displayName || 'Unknown'
    : customer?.attributes?.profile?.displayName || 'Unknown';
  const lastMessageUserId = messageNotification?.lastMessageUserId || null;
  const messageSenderDisplayName =
    lastMessageUserId && lastMessageUserId === provider?.id?.uuid
      ? provider?.attributes?.profile?.displayName || ''
      : lastMessageUserId && lastMessageUserId === customer?.id?.uuid
      ? customer?.attributes?.profile?.displayName || ''
      : '';

  const bookingCreatedAt = transaction?.attributes?.createdAt
    ? moment(transaction.attributes.createdAt).tz(tz)
    : null;
  const lastTransitionedAt = transaction?.attributes?.lastTransitionedAt
    ? moment(transaction.attributes.lastTransitionedAt).tz(tz)
    : null;
  const latestMessageAt = messageNotification?.lastMessageAt
    ? moment(messageNotification.lastMessageAt).tz(tz)
    : null;
  const latestActivityMs = Math.max(
    bookingCreatedAt ? bookingCreatedAt.valueOf() : 0,
    lastTransitionedAt ? lastTransitionedAt.valueOf() : 0,
    latestMessageAt ? latestMessageAt.valueOf() : 0
  );
  const latestActivityMoment = latestMessageAt || lastTransitionedAt || bookingCreatedAt;
  const latestActivityRelative = latestActivityMoment ? latestActivityMoment.fromNow() : '';

  const listingTitle = localizedTitle || safeListing?.attributes?.title || '';
  const listingLocation =
    localizedPublicData?.location?.address ||
    safeListing?.attributes?.publicData?.location?.address ||
    'Unknown Location';
  const txId = transaction?.id?.uuid || '';
  const bookingDate = transaction?.attributes?.createdAt
    ? moment(transaction.attributes.createdAt)
        .tz(tz)
        .format('MMM DD, YYYY')
    : 'Unknown Date';
  const bookingSlot = startZ && endZ ? formatRangeInTZ(startZ, endZ) : 'Awaiting details';
  const attentionNotification = getAttentionNotificationData({
    bookingDate,
    bookingSlot,
    isAttentionNotification: attentionTxIdSet.has(txId),
    isCustomer,
    latestActivityRelative,
    status,
  });

  return {
    attentionNotification,
    author: provider,
    authorDisplayName: provider?.attributes?.profile?.displayName || '',
    authorId: provider?.id?.uuid || '',
    authorProfileImage: getSquareSmallImageUrl(provider),
    bookingDate,
    bookingDateMs: bookingCreatedAt ? bookingCreatedAt.valueOf() : 0,
    bookingSlot,
    customer: customer,
    customerDisplayName: customer?.attributes?.profile?.displayName || '',
    customerProfileImage: getSquareSmallImageUrl(customer),
    dateTime: bookingSlot,
    end,
    hasAttentionNotification: Boolean(attentionNotification),
    hasUnreadMessageNotification: Number(messageNotification?.unreadCount || 0) > 0,
    id: txId,
    image: getInitialImage(safeListing),
    isCustomer,
    latestActivityMs,
    canReview: canLeaveReview(transaction, transactionRole),
    latestActivityRelative,
    listingTitle,
    location: listingLocation,
    messageSenderDisplayName,
    overTime,
    payment: formatTHBFromCents(paymentAmount),
    paymentCents: paymentAmount,
    property: listingTitle || 'Unknown Property',
    reviews: reviews || [],
    shootingDate: bookingSlot,
    shootingStartMs: startZ ? startZ.valueOf() : 0,
    start,
    status,
    statusLabel: label,
    unreadMessageCount: Number(messageNotification?.unreadCount || 0),
    userName,
    durationType,
    _lastTransition: lastTransition,
    _isDeclineTranistions: !!isDeclineTranistions,
  };
};

// ----------------------------
// Filters
// ----------------------------
const filterByLocation = (data, searchLocation) => {
  if (!searchLocation) return data;
  const loc = String(searchLocation).toLowerCase();
  return data.filter(({ location, listingTitle }) => {
    const locationMatch = String(location || '')
      .toLowerCase()
      .includes(loc);
    const titleMatch = String(listingTitle || '')
      .toLowerCase()
      .includes(loc);
    return locationMatch || titleMatch;
  });
};

const filterByPrice = (data, searchPrice) => {
  if (!searchPrice) return data;

  const normalize = s => Number(String(s).replace(/[^0-9.]/g, '') || NaN);
  const target = normalize(searchPrice);

  if (!isFinite(target)) {
    const s = String(searchPrice);
    return data.filter(({ payment }) =>
      String(payment || '')
        .toLowerCase()
        .includes(s.toLowerCase())
    );
  }

  const targetCents = Math.round(target * 100);
  return data.filter(({ paymentCents }) => Number(paymentCents) === targetCents);
};

const filterByDate = (data, searchDate) => {
  if (!searchDate) return data;

  if (Array.isArray(searchDate) && searchDate.length === 2) {
    const from = searchDate[0] ? moment(searchDate[0]).startOf('day') : null;
    const to = searchDate[1] ? moment(searchDate[1]).endOf('day') : null;
    if (!from || !to || !from.isValid() || !to.isValid()) return data;

    return data.filter(({ start }) => {
      const s = start && moment.isMoment(start) ? start : start ? moment(start) : null;
      if (!s || !s.isValid()) return false;
      return s.isSameOrAfter(from) && s.isSameOrBefore(to);
    });
  }

  const searchFormatted = moment(searchDate).format('YYYY-MM-DD');
  return data.filter(({ start, end }) => {
    const startDate = start ? start.format('YYYY-MM-DD') : null;
    const endDate = end ? end.format('YYYY-MM-DD') : null;
    return startDate === searchFormatted || endDate === searchFormatted;
  });
};

// ----------------------------
// Hook: process and filter bookings
// ----------------------------
export const useProcessedBookings = (
  transactions,
  activeTab,
  searchLocation,
  searchPrice,
  searchDate,
  currentUser,
  isDashboard,
  messageNotificationsByTxId = {},
  attentionTxIds = []
) => {
  const attentionTxIdSet = useMemo(() => new Set((attentionTxIds || []).filter(Boolean)), [
    attentionTxIds,
  ]);

  const bookings = useMemo(() => {
    if (!transactions?.length) {
      return {
        [BookingCategory.PAST]: [],
        [BookingCategory.CURRENT]: [],
      };
    }

    const now = moment();
    return transactions.reduce(
      (acc, transaction) => {
        const bookingData = createBookingData(
          {
            ...transaction,
            messageNotification: messageNotificationsByTxId[(transaction?.id?.uuid)],
          },
          currentUser,
          attentionTxIdSet,
          isDashboard
        );
        if (!bookingData) return acc;

        const hasValidEnd = bookingData.end && moment.isMoment(bookingData.end);
        const hasCompletedLifecycle = COMPLETED_BOOKING_STATUSES.has(bookingData.status);
        const category =
          hasCompletedLifecycle || (hasValidEnd && bookingData.end.isBefore(now))
            ? BookingCategory.PAST
            : BookingCategory.CURRENT;

        acc[category].push(bookingData);
        return acc;
      },
      {
        [BookingCategory.PAST]: [],
        [BookingCategory.CURRENT]: [],
      }
    );
  }, [
    transactions,
    currentUser,
    isDashboard,
    messageNotificationsByTxId,
    attentionTxIdSet,
  ]);

  const filteredData = useMemo(() => {
    let data = isDashboard
      ? [...(bookings[BookingCategory.PAST] || []), ...(bookings[BookingCategory.CURRENT] || [])]
      : bookings[activeTab] || [];

    data = filterByLocation(data, searchLocation);
    data = filterByPrice(data, searchPrice);
    data = filterByDate(data, searchDate);

    return data;
  }, [bookings, activeTab, searchLocation, searchPrice, searchDate, isDashboard]);

  return { filteredData, bookings, loading: false };
};

// ----------------------------
// Transition → { status, label, iconType }
// (status = machine key, label = intl id)
// ----------------------------
export function getStatusByTransition(
  lastTransition,
  paymentMethod,
  isCustomer = false,
  transaction = null
) {
  const effectivePaymentMethod = getEffectivePaymentMethod(transaction) || paymentMethod;
  const isPayLater =
    isPayLaterTransaction(transaction) || isPayLaterPaymentMethod(effectivePaymentMethod);
  const isAwaitingDetails = isAwaitingBookingDetails(transaction);
  const result = (status, iconType) => ({
    status,
    label: getBookingStatusIntlKey(status),
    iconType,
  });

  const processStatus = getProcessStatus(transaction);

  if (COMPLETED.has(lastTransition)) return result('completed', 'accepted');

  if (EXPIRED.has(lastTransition)) return result('cancelled', 'cancelled');

  if (PAYMENT_EXPIRED.has(lastTransition)) return result('cancelled', 'cancelled');

  if (FAILED_PAYMENT.has(lastTransition)) return result('payment failed', 'failed');

  if (DECLINED.has(lastTransition)) return result('declined', 'cancelled');

  if (CANCELLED.has(lastTransition)) return result('cancelled', 'cancelled');

  if (DISPUTED.has(lastTransition)) return result('disputed', 'dispute');

  if (REVIEWED.has(lastTransition)) return result('reviewed', 'reviewed');

  // Preserve specific transition labels above (for example, an expired acceptance is displayed as
  // Cancelled), then let the latest terminal process state beat payment/custom-offer metadata that
  // remains on the transaction after confirmation.
  if (TERMINAL_PROCESS_STATUSES.has(processStatus)) {
    const iconType =
      processStatus === BookingStatus.COMPLETED
        ? 'accepted'
        : processStatus === BookingStatus.REVIEWED
        ? 'reviewed'
        : processStatus === BookingStatus.DISPUTED
        ? 'dispute'
        : 'cancelled';
    return result(processStatus, iconType);
  }

  if (!lastTransition) {
    return isAwaitingDetails
      ? result(BookingStatus.AWAITING_DETAILS, 'request')
      : result('pending', 'request');
  }

  if (lastTransition === TRANSITIONS.TRANSITION_INQUIRE) return result('inquiry', 'inquiry');

  if (hasPendingRescheduleRequest(transaction)) {
    return result(BookingStatus.RESCHEDULE_REQUESTED, 'reschedule');
  }

  if (PAY_LATER_ACCEPTED.has(lastTransition)) {
    return isOmisePaymentPaid(transaction)
      ? result(BookingStatus.CONFIRMED, 'accepted')
      : result(BookingStatus.PENDING_PAYMENT, 'request');
  }

  if (isCustomOfferPaymentPending(transaction)) {
    return result(BookingStatus.PENDING_PAYMENT, 'request');
  }

  if (isCustomOfferPaymentRequestTransition(lastTransition) && !isOmisePaymentPaid(transaction)) {
    return result(BookingStatus.PENDING_PAYMENT, 'request');
  }

  if (isCustomOfferBookingTransaction(transaction) && isCustomOfferPaymentCompleted(transaction)) {
    return result(BookingStatus.CONFIRMED, 'accepted');
  }

  if (processStatus) {
    const iconType =
      processStatus === BookingStatus.ACCEPTED || processStatus === BookingStatus.CONFIRMED
        ? 'accepted'
        : processStatus === BookingStatus.CANCELLED || processStatus === BookingStatus.DECLINED
        ? 'cancelled'
        : 'request';
    return result(processStatus, iconType);
  }

  // Missing dates are a presentation fallback, never a substitute for payment/process state.
  if (isAwaitingDetails) return result(BookingStatus.AWAITING_DETAILS, 'request');

  if (isCustomOfferInquiryTransition(lastTransition)) {
    return isCustomOfferPaymentCompleted(transaction)
      ? result(BookingStatus.CONFIRMED, 'accepted')
      : result('inquiry', 'inquiry');
  }

  if (ACCEPT.has(lastTransition)) return result(BookingStatus.CONFIRMED, 'accepted');

  if (RESCHEDULE_ACCPETED.has(lastTransition)) return result(BookingStatus.CONFIRMED, 'accepted');
  if (RESCHEDULE_DECLINED.has(lastTransition)) return result(BookingStatus.CONFIRMED, 'accepted');

  if (PAY_LATER_PAYMENT_COMPLETED.has(lastTransition))
    return result(BookingStatus.CONFIRMED, 'accepted');

  if (PAYMENT_COMPLETED.has(lastTransition) && isPayLater)
    return result(BookingStatus.CONFIRMED, 'accepted');

  if (PAYMENT_COMPLETED.has(lastTransition) && effectivePaymentMethod === PAYMENT_METHODS.CARD)
    return result(BookingStatus.PENDING, 'request');

  if (
    [
      TRANSITIONS.REQUEST_PAYMENT,
      TRANSITIONS.REQUEST_PAYMENT_AFTER_INQUIRY,
      TRANSITIONS.RETRY_PAYMENT_AFTER_FAILED,
    ].includes(lastTransition)
  ) {
    return result(BookingStatus.PENDING_PAYMENT, 'request');
  }

  if (REQUESTED.has(lastTransition)) return result(BookingStatus.PENDING, 'request');

  if (RESCHEDULED.has(lastTransition)) return result('pending', 'reschedule');

  if (PENDING.has(lastTransition)) return result('pending', 'request');

  return result('pending', 'request');
}

// ----------------------------
// Tx panel color helper
// ----------------------------
export const getStatusTxPanelStyle = (
  lastTransition,
  paymentMethod,
  isCustomer = false,
  transaction = null
) => {
  const { status } = getStatusByTransition(lastTransition, paymentMethod, isCustomer, transaction);
  return getStatusStyle(status);
};
